import { withdrawConsentSchema } from "@closer/shared";
import { Button, EmptyState, Spinner } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/apiClient";

/**
 * Onay geçmişi ve izin yönetimi (CLOS-361).
 *
 * Kullanıcı burada **hangi metnin hangi sürümünü ne zaman kabul ettiğini** görür. Sunucu
 * teknik özeti ve iç kimlikleri zaten göndermiyor; ekran da onları uydurmaz.
 */
export type ConsentHistoryRow = {
  documentId: string;
  type: string;
  version: number;
  effectiveFrom: string;
  required: boolean;
  decision: string;
  source: string;
  grantedAt: string;
  current: boolean;
};

const BASLIKLAR: Record<string, string> = {
  terms_of_service: "Kullanım Şartları",
  privacy_policy: "Gizlilik Politikası",
  marketing: "Kampanya ve duyuru e-postaları",
};

const KARARLAR: Record<string, string> = {
  granted: "Onaylandı",
  withdrawn: "Geri çekildi",
};

/** Onayın hangi akıştan geldiği; kullanıcı "bunu ne zaman yaptım?" sorusunu cevaplayabilsin. */
const KAYNAKLAR: Record<string, string> = {
  registration: "Kayıt sırasında",
  reconsent: "Metin güncellendiğinde",
  profile: "Hesap ayarlarından",
};

function ad(sozluk: Record<string, string>, anahtar: string): string {
  return sozluk[anahtar] ?? anahtar;
}

function tarih(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

export function ConsentsPage() {
  const queryClient = useQueryClient();
  const [hata, setHata] = useState<string | undefined>();

  const { data: satirlar, isLoading } = useQuery({
    queryKey: ["session", "consent-history"],
    queryFn: async () => (await api<ConsentHistoryRow[]>("/api/session/consents")).data,
  });

  const geriCek = useMutation({
    mutationFn: async (documentId: string) => {
      const govde = withdrawConsentSchema.parse({ documentId });
      await api("/api/session/consents/withdraw", {
        method: "POST",
        body: JSON.stringify(govde),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["session", "consent-history"] });
    },
    onError: (err: unknown) => {
      setHata(err instanceof Error ? err.message : "İzin geri çekilemedi");
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const hepsi = satirlar ?? [];
  // Geri çekilebilecekler: yalnız ZORUNLU OLMAYAN ve şu an onaylı olanlar. Zorunlu bir
  // metin için düğme göstermek, sunucunun kesin olarak reddedeceği bir eylemi teklif
  // etmek olurdu.
  const geriCekilebilir = hepsi.filter((s) => s.current && !s.required && s.decision === "granted");

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-slate-900">Onaylarım</h1>
        <p className="text-sm text-slate-600">
          Kabul ettiğiniz metinlerin sürümleri ve tarihleri. Geçmiş kayıtlar silinmez.
        </p>
      </div>

      {hata ? (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      ) : null}

      {geriCekilebilir.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-sm font-medium text-slate-700">İsteğe bağlı izinler</h2>
          {geriCekilebilir.map((s) => (
            <div
              key={s.documentId}
              className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2"
            >
              <span className="text-sm text-slate-700">{ad(BASLIKLAR, s.type)}</span>
              <Button
                variant="secondary"
                disabled={geriCek.isPending}
                onClick={() => {
                  setHata(undefined);
                  geriCek.mutate(s.documentId);
                }}
              >
                İzni geri çek
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      {hepsi.length === 0 ? (
        <EmptyState title="Henüz onay kaydınız yok" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-left text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="py-2 font-medium">Metin</th>
                <th className="py-2 font-medium">Sürüm</th>
                <th className="py-2 font-medium">Karar</th>
                <th className="py-2 font-medium">Kaynak</th>
                <th className="py-2 font-medium">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {hepsi.map((s) => (
                <tr key={`${s.documentId}-${s.grantedAt}`} className="border-t border-slate-100">
                  <td className="py-2 text-slate-700">{ad(BASLIKLAR, s.type)}</td>
                  <td className="py-2 text-slate-600">v{s.version}</td>
                  <td className="py-2 text-slate-600">{ad(KARARLAR, s.decision)}</td>
                  <td className="py-2 text-slate-600">{ad(KAYNAKLAR, s.source)}</td>
                  <td className="py-2 text-slate-600">{tarih(s.grantedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
